import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, detectDevice, loadTokens, saveTokens, tokensFromResponse, ApiError } from "./api.ts";
import type { Clinic, User } from "../../../shared/types.ts";

export interface Me {
  user: User;
  clinic: Clinic | null;
  session: { id: string; device: "mobile" | "desktop"; mobileSessionDays: number; desktopIdleHours: number };
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  expired: boolean;
  login(email: string, password: string): Promise<{ challenge?: "NEW_PASSWORD_REQUIRED"; session?: string }>;
  completeNewPassword(email: string, session: string, newPassword: string): Promise<void>;
  logout(pendingSegments?: number): Promise<void>;
  reload(): Promise<void>;
  setMe(m: Me): void;
}

const Ctx = createContext<AuthState | null>(null);
const ME_CACHE = "aisol-med.me";

function cachedMe(): Me | null {
  try {
    const raw = localStorage.getItem(ME_CACHE);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMeState] = useState<Me | null>(() => (loadTokens() ? cachedMe() : null));
  const [loading, setLoading] = useState(!!loadTokens());
  const [expired, setExpired] = useState(false);

  const setMe = useCallback((m: Me | null) => {
    setMeState(m);
    try {
      if (m) localStorage.setItem(ME_CACHE, JSON.stringify(m));
      else localStorage.removeItem(ME_CACHE);
    } catch {
      /* ignore */
    }
  }, []);

  const reload = useCallback(async () => {
    if (!loadTokens()) {
      setMe(null);
      setLoading(false);
      return;
    }
    try {
      const m = await api<Me>("/me");
      setMe(m);
      setExpired(false);
    } catch (e) {
      // offline: keep the cached profile so that recording keeps working
      if (e instanceof ApiError && e.status === 401) setMe(null);
    } finally {
      setLoading(false);
    }
  }, [setMe]);

  useEffect(() => {
    void reload();
    const onExpired = () => {
      setExpired(true);
      setMe(null);
    };
    window.addEventListener("aisol:session-expired", onExpired);
    return () => window.removeEventListener("aisol:session-expired", onExpired);
  }, [reload, setMe]);

  const finishLogin = useCallback(
    async (r: Parameters<typeof tokensFromResponse>[0]) => {
      saveTokens(tokensFromResponse(r));
      setExpired(false);
      await reload();
    },
    [reload],
  );

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      expired,
      setMe,
      reload,
      async login(email, password) {
        const r = await api<{ challenge?: "NEW_PASSWORD_REQUIRED"; session?: string } & Parameters<typeof tokensFromResponse>[0]>("/auth/login", {
          body: { email, password, device: detectDevice() },
          public: true,
        });
        if (r.challenge) return { challenge: r.challenge, session: r.session };
        await finishLogin(r);
        return {};
      },
      async completeNewPassword(email, session, newPassword) {
        const r = await api<Parameters<typeof tokensFromResponse>[0]>("/auth/new-password", { body: { email, session, newPassword, device: detectDevice() }, public: true });
        await finishLogin(r);
      },
      async logout(pendingSegments = 0) {
        const t = loadTokens();
        try {
          if (t) await api("/auth/logout", { body: { refreshToken: t.refreshToken, pendingSegments } });
        } catch {
          /* offline logout still clears the device */
        }
        saveTokens(null);
        setMe(null);
      },
    }),
    [me, loading, expired, setMe, reload, finishLogin],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("AuthProvider missing");
  return v;
}

export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error("not signed in");
  return me;
}

export const hasRole = (me: Me | null, r: User["roles"][number]) => !!me?.user.roles.includes(r);
